import { ComparisonLineChart } from '@practium/features/dashboard';
import type { CustomPanelControls, SectionTab } from '@practium/ui';
import {
	ActionButton,
	FilterChip,
	ListPageChartCard,
	ListPageCharts,
	ListPageLayout,
	Pagination,
	PeriodPanel,
	PromotionCard,
	SectionCard,
} from '@practium/ui';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponseTimeChart } from '../components/Charts/ResponseTimeChart/ResponseTimeChart';
import { UpdateStatusModal } from '../components/Modals/UpdateStatusModal/UpdateStatusModal';
import { ProductRequestsPageSkeleton } from '../components/Skeletons/ProductRequestsPageSkeleton';
import { ProductRequestsTable } from '../components/Tables/ProductRequestsTable/ProductRequestsTable';
import { useProductRequestsPage } from '../hooks/useProductRequestsPage';
import type { ProductRequestsTab } from '../types/productRequest.types';

export const ProductRequestsPage = () => {
	const navigate = useNavigate();
	const {
		filteredRequests,
		stats,
		isLoading,
		activeTab,
		setActiveTab,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort,
		filterValues,
		setFilterValues,
		filterGroups,
		customDateFrom,
		setCustomDateFrom,
		customDateTo,
		setCustomDateTo,
		page,
		setPage,
		totalPages,
		selectedRequest,
		setSelectedRequest,
		handleUpdateStatus
	} = useProductRequestsPage();

	const renderPeriodPanel = useCallback(
		({ close, activate }: CustomPanelControls) => (
			<PeriodPanel
				dateFrom={customDateFrom}
				dateTo={customDateTo}
				onChange={(from, to) => {
					setCustomDateFrom(from);
					setCustomDateTo(to);
					activate();
				}}
				onClose={close}
			/>
		),
		[customDateFrom, customDateTo, setCustomDateFrom, setCustomDateTo]
	);

	if (isLoading) return <ProductRequestsPageSkeleton />;
	const search =
		activeTab == 'pending'
			? 'pendentes'
			: activeTab == 'approved'
				? 'aceitos'
				: activeTab == 'rejected'
					? 'rejeitados'
					: '';
	const tabs: SectionTab<ProductRequestsTab>[] = [
		{
			key: 'pending',
			label: 'Pendentes',
			content: <ProductRequestsTable requests={filteredRequests} onRowClick={setSelectedRequest} />,
		},
		{
			key: 'approved',
			label: 'Aceitos',
			content: <ProductRequestsTable requests={filteredRequests} onRowClick={setSelectedRequest} />,
		},
		{
			key: 'rejected',
			label: 'Rejeitados',
			content: <ProductRequestsTable requests={filteredRequests} onRowClick={setSelectedRequest} />,
		},
		{
			key: 'all',
			label: 'Todos',
			content: <ProductRequestsTable requests={filteredRequests} onRowClick={setSelectedRequest} />,
		},
	];

	return (
		<>
			<ListPageLayout
				sidebarWidth="27rem"
				sidebarW1500="22rem"
				sidebarW1300="19rem"
				sidebarW1200="17rem"
				sidebar={
					<>
						<PromotionCard
							title="Configure os grupos de permissão"
							subtitle="Defina quais grupos podem criar produtos no Practium"
							buttonText="Configurar"
							onButtonClick={() => {
								navigate('/sistema/novo?type=permissions');
							}}
						/>
						<ListPageCharts>
							<ListPageChartCard>
								<ComparisonLineChart
									title="Solicitações na Plataforma"
									mainValue={stats?.line.subtitle}
									trendValue={stats ? `${stats.line.growthPercentage.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%` : undefined}
									trendLabel="vs semana anterior"
									trendDirection={(stats?.line.growthPercentage ?? 0) < 0 ? 'down' : 'up'}
									footerText={stats ? `Solicitações de ${stats.line.period}` : undefined}
									buttonText="Ver Relatório"
									onButtonClick={() => { }}
									data={stats?.line.data ?? []}
									xAxisKey="label"
									line1Key="current"
									line1Label="Semana atual"
									line2Key="previous"
									line2Label="Semana anterior"
									tooltipTitle="Solicitações"
								/>
							</ListPageChartCard>
							<ListPageChartCard>
								<ResponseTimeChart data={stats?.responseTime} />
							</ListPageChartCard>
						</ListPageCharts>
					</>
				}
			>
				<SectionCard
					title="Solicitações de Produtos"
					subtitle="Gerencie os produtos solicitados pelos usuários para serem cadastrados no sistema"
					headerAction={<ActionButton text="Ver Relatório" onClick={() => { }} />}
					tabs={tabs}
					activeTab={activeTab}
					onTabChange={setActiveTab}
					search={true}
					searchQuery={searchQuery}
					onSearch={setSearchQuery}
					searchPlaceholder={`Buscar por produtos ${search}...`}
					sortAsc={sortAsc}
					onToggleSort={toggleSort}
					filterSlot={
						<FilterChip
							values={filterValues}
							onChange={setFilterValues}
							groups={filterGroups}
							placeholder="Filtros"
							customPanels={{ 'periodo:custom': renderPeriodPanel }}
						/>
					}
					footer={<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
				/>
			</ListPageLayout>

			{selectedRequest && (
				<UpdateStatusModal
					request={selectedRequest}
					onClose={() => setSelectedRequest(null)}
					onSave={handleUpdateStatus}
				/>
			)}
		</>
	);
};
